import { Form, Formik } from "formik"
import React, { useEffect, useState } from "react"
import DynamicField from "../dynamic-field/dynamic-field"
import { trackEvent } from "../../modules/analytics"
import { onFormSubmit } from "../../services/form-builder"
import { navigate } from "gatsby"
import ModalWrapper from "../modal-wrapper/modal-wrapper"
import { FaSpinner } from "react-icons/fa"
import { useLocation } from "@reach/router"

import {
  subtitle,
  formField as productFormField,
  modalCheckout,
  modalCheckoutItem,
  modalCheckoutPrice,
  modalFooter,
  modalAccept,
  formRow,
  downloadFormRow,
  formGroup as productFormGroup,
  formSubmit as productFormSubmit,
  thankYouMessage as productThankYouMessage,
  downloadFormField,
  downloadFormFieldLabel,
  downloadFormFieldError,
  downloadFormFieldCheckbox,
  downloadFormFieldCheckboxLabel,
} from "./custom-form.module.css"

import {
  formFieldWrapper as downloadFormFieldWrapper,
  formSubmit as downloadFormSubmit,
  thankYouMessage as downloadThankYouMessage,
  loadingSpinner,
} from "../download-form/download-form.module.css"
import firebase from "gatsby-plugin-firebase"

import { fireVWOEvent } from "../../services/vwo-handler"

const CustomForm = ({ formData, formPosition, theme = "product" }) => {
  const { rows } = formData
  const [user, setUser] = useState({})
  const [isTouched, setIsTouched] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const location = useLocation()

  useEffect(() => {
    if (!firebase) return
    firebase.auth().onAuthStateChanged(async (authUser) => {
      if (!authUser) return await firebase.auth().signInAnonymously()
      setUser(authUser)
    })
  }, [user])

  const style = {
    product: {
      formField: productFormField,
      formRow: formRow,
      formFieldLabel: "",
      formGroup: productFormGroup,
      formSubmit: productFormSubmit,
      formCheckbox: "",
      formCheckboxLabel: "",
      thankYouMessage: productThankYouMessage,
    },
    download: {
      formField: downloadFormField,
      formRow: downloadFormRow,
      formFieldLabel: downloadFormFieldLabel,
      formGroup: downloadFormFieldWrapper,
      formFieldError: downloadFormFieldError,
      formCheckbox: downloadFormFieldCheckbox,
      formCheckboxLabel: downloadFormFieldCheckboxLabel,
      formSubmit: downloadFormSubmit,
      thankYouMessage: downloadThankYouMessage,
    },
  }

  useEffect(() => {
    if (isTouched) {
      trackEvent(`form_start`, {
        form_id: formData.id,
      })
      fireVWOEvent("formStart", { formStart: true })
    }
  }, [isTouched])

  const createBlobAndDownload = async (url) => {
    // Fetch file data using fetch or useAxios
    const response = await fetch(url)

    // Check for successful response
    if (!response.ok) {
      throw new Error("Failed to download file")
    }

    // Create a blob and download it
    const blob = await response.blob()
    const downloadLink = document.createElement("a")
    downloadLink.href = window.URL.createObjectURL(blob)
    downloadLink.download = url.split("/").pop() // Extract filename
    downloadLink.click()
    window.URL.revokeObjectURL(downloadLink.href)
  }

  const getInitialValues = () => {
    let initialValues = {}

    const componentsFlat = rows?.flatMap((row) => {
      return row.components
    })

    componentsFlat?.forEach((fieldData) => {
      const fieldName = fieldData.slug !== "" ? fieldData.slug : fieldData.id
      switch (fieldData.type) {
        case "countryselect":
          initialValues[fieldName] = "NL"
          break
        case "checkbox":
          initialValues[fieldName] = false
          break
        case "addcandidate":
          initialValues[fieldName] = [
            {
              firstName: "",
              lastName: "",
              email: "",
              language: "",
              iq_test: false,
            },
          ]
          break
        case "radio":
          if (fieldData.multiple) {
            initialValues[fieldName] = fieldData.defaultSelected || []
          } else {
            initialValues[fieldName] =
              fieldData.defaultSelected && fieldData.defaultSelected.length > 0
                ? fieldData.defaultSelected[0]
                : ""
          }
          break
        default:
          initialValues[fieldName] = ""
          break
      }
    })
    return initialValues
  }

  const initialValues = getInitialValues()

  function substituteVariables(inputString, variableValues) {
    // Use a regular expression to find all variable names in the input string 
    const variableNames = inputString.match(/\{\{\s*(\w+)\s*\}\}/g)

    if (!variableNames) {
      return inputString
    }

    if (!variableValues || variableValues.length === 0) {
      return inputString
    }

    // Replace each variable name with its corresponding value
    let outputString = inputString
    for (const variableName of variableNames) {
      const key = variableName.slice(2, -2).trim()
      const matchingVariable = variableValues.find((v) => v.type === key)
      if (matchingVariable) {
        outputString = outputString.replace(
          variableName,
          matchingVariable.value
        )
      } else {
        // Handle missing variables (e.g., throw an error, use a default value)
        outputString = outputString.replace(variableName, "")
      }
    }

    return outputString
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          // Check if there is a download event
          if (
            formData.contentfulAssetId
          ) {
            // Fetch file from Contentful CDN
            const asset = formData.contentfulAssetId
            await createBlobAndDownload(asset)
          }

          trackEvent(`form_submit`, {
            form_id: formData.id,
            event_page_position: formPosition,
          })
          fireVWOEvent("formSubmit", { clickOnButton: true })
          if (formData.usePaymentProvider) {
            const { checkoutUrl } = await onFormSubmit(
              formData,
              values,
              location.pathname
            )
            navigate(checkoutUrl)
          } else {
            onFormSubmit(formData, values, location.pathname)
            if (formData?.submissionLink) {
              const variablesList = Object.entries(values).map(
                ([key, value]) => ({ type: key, value: value })
              )
              const parsedLink = substituteVariables(
                formData?.submissionLink,
                variablesList
              )
              window.location.href = parsedLink
            }
          }

          setSubmitted(true)
        } catch (err) {
          console.log(err)
          setSubmitting(false)
        }
      }}
    >
      {({
        touched,
        values,
        isSubmitting,
        submitForm,
        errors,
        handleChange,
      }) => {
        if (Object.values(touched).some((val) => val === true))
          setIsTouched(true)
        return (
          <Form style={{ width: "100%" }}>
            {/* FORM FIELDS */}
            {rows?.map((row) => {
              return (
                <div className={style[theme].formRow} key={row.id}>
                  {row.components?.map((rowItem) => {
                    return (
                      <div className={style[theme].formGroup} key={rowItem.id}>
                        <DynamicField
                          fieldData={rowItem}
                          values={values}
                          submitted={submitted}
                          handleChange={handleChange}
                          initialValues={initialValues}
                          formId={formData.id}
                          style={style[theme]}
                          theme={theme}
                          errors={errors}
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}

            {/* FORM SUBMIT  */}
            {!submitted && (
              <>
                {!formData.usePaymentProvider && (
                  <button
                    className={style[theme].formSubmit}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {formData.buttonText
                      ? formData.buttonText
                      : "Meer informatie aanvragen"}
                  </button>
                )}
                {formData.usePaymentProvider && (
                  <button
                    className={style[theme].formSubmit}
                    type="button"
                    onClick={() => setShowModal(true)}
                    disabled={isSubmitting}
                  >
                    {formData.buttonText
                      ? formData.buttonText
                      : "Meer informatie aanvragen"}
                  </button>
                )}
                {theme === "product" && (
                  <span style={{ color: "gray" }}> * verplicht veld</span>
                )}
              </>
            )}

            {submitted && (
              <div className={style[theme].thankYouMessage}>
                <h4>
                  {formData.postSubmissionTitle
                    ? formData.postSubmissionTitle
                    : "Bedankt!"}
                </h4>
                <p>
                  {formData.postSubmissionBody
                    ? formData.postSubmissionBody
                    : "We hebben je gegevens ontvangen, we nemen spoedig contact met je op."}
                </p>
              </div>
            )}

            {/* Payment provider modal */}
            {formData.usePaymentProvider && (
              <ModalWrapper
                showModal={showModal}
                setShowModal={setShowModal}
                title="Besteloverzicht"
              >
                <div className={modalCheckout}>
                  <div>
                    <div className={modalCheckoutItem}>
                      <span>{formData.productName}</span>
                    </div>
                    <span className={modalCheckoutPrice}>
                      €
                      {Number(
                        formData.productPrice *
                          (formData.priceMultiplier &&
                          formData.priceMultiplier.id !== ""
                            ? values[
                                formData.priceMultiplier.slug !== ""
                                  ? formData.priceMultiplier.slug
                                  : formData.priceMultiplier.id
                              ]?.length
                            : 1)
                      )
                        .toFixed(2)
                        .replaceAll(".", ",")}
                    </span>
                  </div>
                  <div>
                    <span>Subtotaal</span>
                    <span className={modalCheckoutPrice}>
                      €
                      {Number(
                        formData.productPrice *
                          (formData.priceMultiplier &&
                          formData.priceMultiplier.id !== ""
                            ? values[
                                formData.priceMultiplier?.slug !== ""
                                  ? formData.priceMultiplier.slug
                                  : formData.priceMultiplier.id
                              ]?.length
                            : 1)
                      )
                        .toFixed(2)
                        .replaceAll(".", ",")}
                    </span>
                  </div>
                  <div>
                    <span>Btw (21%)</span>
                    <span className={modalCheckoutPrice}>
                      €
                      {(
                        Number(
                          formData.productPrice *
                            (formData.priceMultiplier &&
                            formData.priceMultiplier.id !== ""
                              ? values[
                                  formData.priceMultiplier?.slug !== ""
                                    ? formData.priceMultiplier.slug
                                    : formData.priceMultiplier.id
                                ]?.length
                              : 1)
                        ) * 0.21
                      )
                        .toFixed(2)
                        .replaceAll(".", ",")}
                    </span>
                  </div>
                  <div>
                    <span>Totaal</span>
                    <span className={modalCheckoutPrice}>
                      €
                      {(
                        Number(
                          formData.productPrice *
                            (formData.priceMultiplier &&
                            formData.priceMultiplier.id !== ""
                              ? values[
                                  formData.priceMultiplier?.slug !== ""
                                    ? formData.priceMultiplier.slug
                                    : formData.priceMultiplier.id
                                ]?.length
                              : 1)
                        ) * 1.21
                      )
                        .toFixed(2)
                        .replaceAll(".", ",")}
                    </span>
                  </div>
                </div>

                <div className={modalFooter}>
                  <button
                    className={modalAccept}
                    style={{ margin: "20px 0px" }}
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      submitForm()
                    }}
                  >
                    {isSubmitting && (
                      <FaSpinner
                        className={loadingSpinner}
                        style={{ height: "24px" }}
                      />
                    )}
                    {isSubmitting ? "Betaalpagina laden" : "Betalen"}
                  </button>
                </div>
              </ModalWrapper>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default CustomForm
